import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { redeemReward } from "./cashierApi";
import { newIdempotencyKey } from "./idempotency";

/**
 * The rewards a guest can walk out with right now, each with a [Redeem] button.
 *
 * Extracted so the till's TWO routes to a redemption share ONE implementation:
 *
 *  • the guest strip — someone already on the board (they ordered, or they
 *    tapped "I'm at the counter" on their card);
 *  • the search panel — the fallback for anyone else, because a guest who
 *    could not or did not tap still has to be servable. That is the whole
 *    reason this is a component and not two copies: the idempotency-key
 *    discipline below is what stands between a double-tap and a double charge
 *    (F-16), and a second hand-written copy of it is a second chance to get it
 *    wrong.
 *
 * REDEMPTION IS A TILL ACTION. The customer card never gets one of these — it
 * is view-only by construction, which is the entire reason a signed token in a
 * URL is an acceptable identity. A guest can ring the doorbell; only the
 * counter can spend.
 *
 * Offered only when the guest can afford something AND their F-1 gate is open.
 * A locked guest is told why, rather than shown a button that would 422 in
 * their face.
 */
export default function RedeemList({ token, guest, rewards, onRedeemed, wantsRewardId }) {
  const { t, i18n } = useTranslation();
  const isAr = (i18n.resolvedLanguage || i18n.language || "en").startsWith("ar");

  const [busy, setBusy] = useState({});
  const [note, setNote] = useState(null);   // { kind: 'ok'|'err', text }

  // One key per redemption, regenerated only after a SUCCESSFUL burn, so every
  // retry of the same redemption carries the same key and collapses onto the
  // original ledger row instead of charging again.
  const idemKey = useRef(newIdempotencyKey());

  const rewardName = useMemo(() => (r) => (isAr && r.name_ar ? r.name_ar : r.name), [isAr]);

  const ready = useMemo(
    () => (guest?.can_redeem ? (rewards || []).filter((r) => (guest.balance ?? 0) >= r.cost) : []),
    [guest, rewards]
  );

  if (!guest) return null;

  const redeem = async (reward) => {
    if (busy[reward.id]) return;
    setBusy((b) => ({ ...b, [reward.id]: true }));
    setNote(null);
    try {
      const res = await redeemReward(token, {
        customerId: guest.id,
        rewardId: reward.id,
        idempotencyKey: idemKey.current,
      });
      idemKey.current = newIdempotencyKey();
      setNote({
        kind: "ok",
        text: res.charged
          ? t("cashier.redeem.done", { reward: rewardName(reward), balance: res.points?.balance ?? 0 })
          : t("cashier.redeem.already", { reward: rewardName(reward) }),
      });
      onRedeemed?.(res);
    } catch (e) {
      const code = e?.response?.data?.error;
      setNote({
        kind: "err",
        text:
          code === "redemption_locked" ? t("cashier.redeem.err_locked")
          : code === "insufficient_points" ? t("cashier.redeem.err_points")
          : t("cashier.redeem.err_generic"),
      });
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[reward.id]; return n; });
    }
  };

  return (
    <>
      {!guest.can_redeem ? (
        <div className="cds-guest-none locked">{t("cashier.redeem.locked_hint")}</div>
      ) : ready.length === 0 ? (
        <div className="cds-guest-none">{t("cashier.redeem.none_affordable")}</div>
      ) : (
        <ul className="cds-ready">
          {ready.map((r) => (
            <li key={r.id} className={wantsRewardId === r.id ? "wanted" : undefined}>
              <span className="rl">
                {t("cashier.redeem.ready", { reward: rewardName(r) })}
                <span className="cost mono">−{r.cost}</span>
                {/* Which one they came in for, when the card told us. Advisory:
                    a cashier facing a guest who can afford three shouldn't have
                    to ask. */}
                {wantsRewardId === r.id && (
                  <span className="wanted-tag">{t("cashier.redeem.wanted")}</span>
                )}
              </span>
              <button type="button" className="cds-redeem" disabled={!!busy[r.id]}
                onClick={() => redeem(r)}>
                {busy[r.id] ? <span className="cds-spin" /> : t("cashier.redeem.cta")}
              </button>
            </li>
          ))}
        </ul>
      )}

      {note && <div className={`cds-guest-note ${note.kind}`}>{note.text}</div>}
    </>
  );
}
